import { useEffect, useRef, useState } from 'react'
import { Button, Card, CardContent, cn } from '@saas-pages/shared/ui'
import { toolInvoke, toolSuccess, toolError } from '@saas-pages/shared/public/runtime'
import { format, useFormatBytes, useT, type Translations } from '@/shared/i18n'
import {
  LEVEL_VALUES,
  MAX_FILE_MB,
  validateFileSize,
  savingsPercent,
  compressedFilename,
  compressFileWasm,
  classifyPdf,
  decideStrategy,
  type CompressionLevel,
  type PdfClassification,
} from './lib'
import {
  compressionAnalyticsParams,
  coverageBand,
  type CompressionAnalyticsDetails,
  type CompressionSource,
} from './analytics'
import {
  TARGET_SIZE_PRESETS,
  targetBytesForPreset,
  type CompressionLandingSource,
  type CompressionTargetPreset,
} from './targetPreset'
import { FilePickPrompt } from '@/shared/ui/FilePickPrompt'
import { RevealOnMount } from '@/shared/ui/RevealOnMount'

interface SzhatFormProps {
  initialTargetPreset?: CompressionTargetPreset
  initialLandingSource?: CompressionLandingSource
  defaultLevel?: CompressionLevel
  classifyOverride?: (file: File) => Promise<PdfClassification>
  localCompressOverride?: (
    file: File,
    level: CompressionLevel,
    signal: AbortSignal,
  ) => Promise<Blob>
}

const COMPRESS_UTILITY = 'compress'
const WEAK_SAVINGS_RATIO = 0.85

// ─── State machine ────────────────────────────────────────────────────────────

type Phase =
  | { kind: 'idle' }
  | { kind: 'ready'; file: File }
  | { kind: 'classifying'; file: File }
  | { kind: 'noop'; file: File; reason: 'vector-only' }
  | { kind: 'loading'; file: File; level: CompressionLevel }
  | {
      kind: 'success'
      file: File
      level: CompressionLevel
      blob: Blob
      objectUrl: string
    }
  | {
      kind: 'error'
      file: File | null
      level: CompressionLevel
      message: string
      errorCode: string
    }

function isEncryptedPdfError(error: unknown): boolean {
  return (
    error instanceof Error
    && /encrypt|password/i.test(`${error.name} ${error.message}`)
  )
}

const LEVEL_LABEL_KEYS: Record<CompressionLevel, { label: keyof Translations['szhat']; hint: keyof Translations['szhat'] }> = {
  screen: { label: 'levelStrong', hint: 'levelStrongHint' },
  ebook: { label: 'levelStandard', hint: 'levelStandardHint' },
  printer: { label: 'levelMin', hint: 'levelMinHint' },
}

// ─── Drop-zone ────────────────────────────────────────────────────────────────

interface DropZoneProps {
  onFile: (file: File) => void
  disabled: boolean
}

function DropZone({ onFile, disabled }: DropZoneProps) {
  const t = useT()
  const inputRef = useRef<HTMLInputElement>(null)
  const [dragging, setDragging] = useState(false)

  function handleFiles(files: FileList | null) {
    if (!files || files.length === 0) return
    const file = files[0]
    if (!file) return
    if (!file.name.toLowerCase().endsWith('.pdf')) return
    onFile(file)
  }

  return (
    <div
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-label={t.common.pickPdf}
      onClick={() => !disabled && inputRef.current?.click()}
      onKeyDown={(e) => {
        if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault()
          inputRef.current?.click()
        }
      }}
      onDragOver={(e) => {
        e.preventDefault()
        if (!disabled) setDragging(true)
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => {
        e.preventDefault()
        setDragging(false)
        if (!disabled) handleFiles(e.dataTransfer.files)
      }}
      className={cn(
        'flex flex-col items-center justify-center gap-3',
        'rounded-xl border-2 border-dashed p-6 sm:p-10 text-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        'cursor-pointer select-none transition-colors',
        dragging && !disabled
          ? 'border-primary bg-primary/5'
          : 'border-border hover:border-primary/50 hover:bg-accent/30',
        disabled && 'pointer-events-none opacity-50',
      )}
    >
      {/* Upload icon */}
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="40"
        height="40"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="text-primary/60"
        aria-hidden="true"
      >
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
        <polyline points="17 8 12 3 7 8" />
        <line x1="12" y1="3" x2="12" y2="15" />
      </svg>

      <div>
        <p className="text-sm font-medium text-foreground">
          <FilePickPrompt drag={t.common.dragPdf} />
        </p>
        <p className="mt-1 text-xs text-muted-foreground">
          {format(t.common.pdfLimit, { size: MAX_FILE_MB })}
        </p>
        <p className="mt-1 flex items-center justify-center gap-1 text-[11px] text-muted-foreground/80">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="11"
            height="11"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
          {t.common.localBadge}
        </p>
      </div>

      <input
        ref={inputRef}
        type="file"
        accept=".pdf,application/pdf"
        className="sr-only"
        tabIndex={-1}
        onChange={(e) => handleFiles(e.target.files)}
        disabled={disabled}
      />
    </div>
  )
}

// ─── File badge ───────────────────────────────────────────────────────────────

interface FileBadgeProps {
  file: File
  onClear: () => void
  disabled: boolean
}

function FileBadge({ file, onClear, disabled }: FileBadgeProps) {
  const t = useT()
  const formatBytes = useFormatBytes()
  return (
    <div className="flex items-center gap-3 rounded-lg border bg-accent/30 px-4 py-3">
      {/* PDF icon */}
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="shrink-0 text-primary"
        aria-hidden="true"
      >
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
      </svg>

      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-foreground">{file.name}</p>
        <p className="text-xs text-muted-foreground">{formatBytes(file.size)}</p>
      </div>

      <button
        type="button"
        onClick={onClear}
        disabled={disabled}
        aria-label={t.common.deleteFile}
        className={cn(
          'shrink-0 rounded p-1 text-muted-foreground transition-colors',
          'hover:bg-destructive/10 hover:text-destructive',
          disabled && 'pointer-events-none opacity-50',
        )}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
    </div>
  )
}

// ─── Level selector ───────────────────────────────────────────────────────────

interface LevelSelectorProps {
  value: CompressionLevel
  onChange: (v: CompressionLevel) => void
  disabled: boolean
}

function LevelSelector({ value, onChange, disabled }: LevelSelectorProps) {
  const t = useT()
  return (
    <fieldset disabled={disabled} className="space-y-2">
      <legend className="mb-2 text-sm font-medium text-foreground">{t.szhat.levelLegend}</legend>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
        {LEVEL_VALUES.map((v) => {
          const selected = value === v
          const keys = LEVEL_LABEL_KEYS[v]
          return (
            <label
              key={v}
              className={cn(
                'flex cursor-pointer flex-col gap-1 rounded-lg border p-3 transition-colors',
                selected
                  ? 'border-primary bg-primary/5 ring-1 ring-primary'
                  : 'border-border hover:border-primary/40 hover:bg-accent/20',
                disabled && 'pointer-events-none opacity-50',
              )}
            >
              <input
                type="radio"
                name="compression-level"
                value={v}
                checked={selected}
                onChange={() => onChange(v)}
                className="sr-only"
              />
              <span className="text-sm font-medium text-foreground">{t.szhat[keys.label]}</span>
              <span className="text-xs text-muted-foreground">{t.szhat[keys.hint]}</span>
            </label>
          )
        })}
      </div>
    </fieldset>
  )
}

interface TargetSizeSelectorProps {
  value: CompressionTargetPreset
  onChange: (value: CompressionTargetPreset) => void
  disabled: boolean
}

function TargetSizeSelector({ value, onChange, disabled }: TargetSizeSelectorProps) {
  const t = useT()

  return (
    <fieldset disabled={disabled} className="space-y-2">
      <legend className="mb-2 text-sm font-medium text-foreground">
        {t.szhat.targetLegend}
      </legend>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {TARGET_SIZE_PRESETS.map((preset) => {
          const selected = value === preset
          const title = preset === 'none' ? t.szhat.targetNone : t.szhat.targetFiveMb
          const hint = preset === 'none'
            ? t.szhat.targetNoneHint
            : t.szhat.targetFiveMbHint

          return (
            <label
              key={preset}
              className={cn(
                'flex cursor-pointer flex-col gap-1 rounded-lg border p-3 transition-colors',
                selected
                  ? 'border-primary bg-primary/5 ring-1 ring-primary'
                  : 'border-border hover:border-primary/40 hover:bg-accent/20',
                disabled && 'pointer-events-none opacity-50',
              )}
            >
              <input
                type="radio"
                name="compression-target"
                value={preset}
                checked={selected}
                onChange={() => onChange(preset)}
                className="sr-only"
              />
              <span className="text-sm font-medium text-foreground">{title}</span>
              <span className="text-xs text-muted-foreground">{hint}</span>
            </label>
          )
        })}
      </div>
      {value === '5mb' && (
        <p className="text-xs text-muted-foreground">{t.szhat.targetBestEffort}</p>
      )}
    </fieldset>
  )
}

// ─── Noop panel ───────────────────────────────────────────────────────────────

interface NoopPanelProps {
  file: File
  targetBytes?: number
  onReset: () => void
}

function NoopPanel({ file, targetBytes, onReset }: NoopPanelProps) {
  const t = useT()
  const formatBytes = useFormatBytes()
  const targetHit = targetBytes != null && file.size <= targetBytes

  function handleDownload() {
    const url = URL.createObjectURL(file)
    const a = document.createElement('a')
    a.href = url
    a.download = file.name
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <Card className="border-border bg-muted/30">
      <CardContent className="p-5">
        <RevealOnMount />
        <div className="mb-3 flex items-center gap-2">
          {/* Info icon */}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="shrink-0 text-muted-foreground"
            aria-hidden="true"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="16" x2="12" y2="12" />
            <line x1="12" y1="8" x2="12.01" y2="8" />
          </svg>
          <span className="text-base font-semibold text-foreground">{t.szhat.noopTitle}</span>
        </div>

        <p className="mb-3 text-sm text-muted-foreground">{t.szhat.noopBody}</p>

        <p className="mb-4 text-sm text-foreground">
          <span className="text-muted-foreground">{t.common.size}: </span>
          <span className="font-medium">{formatBytes(file.size)}</span>
        </p>

        {targetBytes != null && (
          <div
            className={cn(
              'mb-4 rounded-lg border p-3 text-sm',
              targetHit
                ? 'border-green-200 bg-green-50 text-green-800 dark:border-green-500/30 dark:bg-green-500/10 dark:text-green-200'
                : 'border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200',
            )}
          >
            <p className="font-medium">
              {targetHit ? t.szhat.targetHitTitle : t.szhat.targetMissTitle}
            </p>
            <p className="mt-1 text-xs">
              {targetHit
                ? format(t.szhat.targetHitBody, { target: formatBytes(targetBytes) })
                : format(t.szhat.targetNoopMissBody, { target: formatBytes(targetBytes) })}
            </p>
          </div>
        )}

        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <Button onClick={handleDownload} size="lg" className="flex-1">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            {t.szhat.noopDownload}
          </Button>
          <Button variant="outline" onClick={onReset} className="flex-1 sm:flex-none">
            {t.szhat.again}
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

// ─── Result panel ─────────────────────────────────────────────────────────────

interface ResultPanelProps {
  file: File
  blob: Blob
  objectUrl: string
  level: CompressionLevel
  targetBytes?: number
  onReset: () => void
  onTryStronger: () => void
}

function ResultPanel({
  file,
  blob,
  objectUrl,
  level,
  targetBytes,
  onReset,
  onTryStronger,
}: ResultPanelProps) {
  const t = useT()
  const formatBytes = useFormatBytes()
  const savings = savingsPercent(file.size, blob.size)
  const savingsNegative = file.size > blob.size
  const targetHit = targetBytes != null && blob.size <= targetBytes
  const targetMiss = targetBytes != null && !targetHit
  const weakSavings = file.size > 0 && blob.size / file.size > WEAK_SAVINGS_RATIO

  function handleDownload() {
    const a = document.createElement('a')
    a.href = objectUrl
    a.download = compressedFilename(file.name)
    a.click()
  }

  return (
    <Card className="border-green-200 bg-green-50/50 dark:border-green-500/30 dark:bg-green-500/10">
      <CardContent className="p-5">
        <RevealOnMount />
        <div className="mb-4 flex items-center gap-2">
          {/* Check icon */}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="shrink-0 text-green-600 dark:text-green-300"
            aria-hidden="true"
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
          <span className="text-base font-semibold text-green-700 dark:text-green-200">
            {t.szhat.resultTitle}
          </span>
        </div>

        <div className="mb-4 space-y-1.5 text-sm">
          <div className="flex justify-between gap-2">
            <span className="text-muted-foreground">{t.common.file}</span>
            <span className="truncate text-right font-medium text-foreground">{file.name}</span>
          </div>
          <div className="flex justify-between gap-2">
            <span className="text-muted-foreground">{t.szhat.sizeBefore}</span>
            <span className="font-medium text-foreground">{formatBytes(file.size)}</span>
          </div>
          <div className="flex justify-between gap-2">
            <span className="text-muted-foreground">{t.szhat.sizeAfter}</span>
            <span className="font-medium text-foreground">{formatBytes(blob.size)}</span>
          </div>
          {targetBytes != null && (
            <div className="flex justify-between gap-2">
              <span className="text-muted-foreground">{t.szhat.targetLimit}</span>
              <span className="font-medium text-foreground">{formatBytes(targetBytes)}</span>
            </div>
          )}
          <div className="flex justify-between gap-2">
            <span className="text-muted-foreground">{t.szhat.savings}</span>
            <span
              className={cn(
                'font-semibold',
                savingsNegative ? 'text-green-600 dark:text-green-300' : 'text-orange-500 dark:text-orange-300',
              )}
            >
              {savings}
            </span>
          </div>
        </div>

        {targetBytes != null && (
          <div
            className={cn(
              'mb-4 rounded-lg border p-3 text-sm',
              targetHit
                ? 'border-green-200 bg-green-50 text-green-800 dark:border-green-500/30 dark:bg-green-500/10 dark:text-green-200'
                : 'border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200',
            )}
          >
            <p className="font-medium">
              {targetHit ? t.szhat.targetHitTitle : t.szhat.targetMissTitle}
            </p>
            <p className="mt-1 text-xs">
              {targetHit
                ? format(t.szhat.targetHitBody, { target: formatBytes(targetBytes) })
                : format(t.szhat.targetMissBody, { target: formatBytes(targetBytes) })}
            </p>
            {targetMiss && level !== 'screen' && (
              <Button variant="outline" size="sm" className="mt-3" onClick={onTryStronger}>
                {t.szhat.targetTryStronger}
              </Button>
            )}
          </div>
        )}

        {weakSavings && !targetMiss && (
          <p className="mb-4 text-xs text-muted-foreground">{t.szhat.weakSavingsHint}</p>
        )}

        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <Button onClick={handleDownload} size="lg" className="flex-1">
            {/* Download icon */}
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            {t.common.download}
          </Button>
          <Button variant="outline" onClick={onReset} className="flex-1 sm:flex-none">
            {t.szhat.again}
          </Button>
        </div>

      </CardContent>
    </Card>
  )
}

// ─── Error panel ──────────────────────────────────────────────────────────────

interface ErrorPanelProps {
  message: string
  onRetry: () => void
  onCancel: () => void
}

function ErrorPanel({ message, onRetry, onCancel }: ErrorPanelProps) {
  const t = useT()

  return (
    <Card className="border-destructive/30 bg-destructive/5">
      <CardContent className="p-5">
        <div className="mb-3 flex items-center gap-2">
          {/* Alert icon */}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="shrink-0 text-destructive"
            aria-hidden="true"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <span className="text-sm font-semibold text-destructive">{t.common.errorTitle}</span>
        </div>
        <p className="mb-4 text-sm text-foreground">{message}</p>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={onRetry}>
            {t.szhat.retryLocal}
          </Button>
          <Button variant="ghost" size="sm" onClick={onCancel}>
            {t.szhat.chooseAnotherFile}
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

// ─── Spinner ──────────────────────────────────────────────────────────────────

function Spinner() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="animate-spin"
      aria-hidden="true"
    >
      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
    </svg>
  )
}

// ─── Main form ────────────────────────────────────────────────────────────────

export function SzhatForm({
  initialTargetPreset = 'none',
  initialLandingSource = 'direct',
  defaultLevel,
  classifyOverride = classifyPdf,
  localCompressOverride = compressFileWasm,
}: SzhatFormProps) {
  const t = useT()
  const [phase, setPhase] = useState<Phase>({ kind: 'idle' })
  const [targetPreset, setTargetPreset] =
    useState<CompressionTargetPreset>(initialTargetPreset)
  const [landingSource, setLandingSource] =
    useState<CompressionLandingSource>(initialLandingSource)
  const [level, setLevel] = useState<CompressionLevel>(
    defaultLevel ?? (initialTargetPreset === '5mb' ? 'screen' : 'ebook'),
  )
  const abortRef = useRef<AbortController | null>(null)
  const operationGenerationRef = useRef(0)
  const objectUrlRef = useRef<string | null>(null)
  const targetBytes = targetBytesForPreset(targetPreset)
  const source: CompressionSource =
    landingSource === 'legacy_redirect' ? 'target-size-5mb' : 'compress'
  const utility = COMPRESS_UTILITY

  const isLoading = phase.kind === 'loading' || phase.kind === 'classifying'
  const hasFile =
    phase.kind === 'ready' ||
    phase.kind === 'loading' ||
    phase.kind === 'classifying' ||
    phase.kind === 'error'
  const currentFile = hasFile ? phase.file : null

  useEffect(() => {
    setTargetPreset(initialTargetPreset)
    setLandingSource(initialLandingSource)
    if (initialTargetPreset === '5mb') setLevel('screen')
  }, [initialLandingSource, initialTargetPreset])

  useEffect(() => () => {
    operationGenerationRef.current += 1
    abortRef.current?.abort()
    if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current)
  }, [])

  function replaceObjectUrl(nextUrl: string | null) {
    if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current)
    objectUrlRef.current = nextUrl
  }

  function isCurrentOperation(generation: number, controller: AbortController) {
    return (
      operationGenerationRef.current === generation &&
      !controller.signal.aborted
    )
  }

  function analyticsParams(
    currentLevel: CompressionLevel,
    details?: CompressionAnalyticsDetails,
  ) {
    return compressionAnalyticsParams(
      source,
      landingSource,
      currentLevel,
      targetBytes != null,
      details,
    )
  }

  function handleFile(file: File) {
    operationGenerationRef.current += 1
    abortRef.current?.abort()
    if (!validateFileSize(file)) {
      toolError(utility, 'client_too_large', analyticsParams(level, {
        failureCategory: 'size_limit',
      }))
      setPhase({
        kind: 'error',
        file,
        level,
        errorCode: 'client_too_large',
        message: format(t.common.tooLarge, { size: MAX_FILE_MB }),
      })
      return
    }

    replaceObjectUrl(null)

    setPhase({ kind: 'ready', file })
  }

  function handleClear() {
    operationGenerationRef.current += 1
    replaceObjectUrl(null)
    abortRef.current?.abort()
    setPhase({ kind: 'idle' })
  }

  function handleRetry() {
    if ((phase.kind === 'error' || phase.kind === 'noop') && phase.file) {
      setPhase({ kind: 'ready', file: phase.file })
    } else {
      setPhase({ kind: 'idle' })
    }
  }

  async function runLocalCompress(file: File, currentLevel: CompressionLevel) {
    const controller = new AbortController()
    abortRef.current = controller
    const generation = ++operationGenerationRef.current

    // 1. Classify
    setPhase({ kind: 'classifying', file })
    toolInvoke(utility, analyticsParams(currentLevel))

    try {
      const cls = await classifyOverride(file)
      if (!isCurrentOperation(generation, controller)) return
      const strategy = decideStrategy(cls)

      if (strategy.kind === 'noop') {
        toolSuccess(utility, 0, analyticsParams(currentLevel, {
          mode: 'noop',
          coverageBand: coverageBand(cls),
          targetMet: targetBytes == null ? undefined : file.size <= targetBytes,
        }))
        setPhase({ kind: 'noop', file, reason: 'vector-only' })
        return
      }

      // 2. WASM compress
      setPhase({ kind: 'loading', file, level: currentLevel })
      const startMs = Date.now()

      try {
        const blob = await localCompressOverride(file, currentLevel, controller.signal)
        if (!isCurrentOperation(generation, controller)) return
        const durationMs = Date.now() - startMs
        const objectUrl = URL.createObjectURL(blob)
        replaceObjectUrl(objectUrl)

        toolSuccess(utility, durationMs, analyticsParams(currentLevel, {
          mode: 'wasm',
          coverageBand: coverageBand(cls),
          targetMet: targetBytes == null ? undefined : blob.size <= targetBytes,
        }))

        setPhase({
          kind: 'success',
          file,
          level: currentLevel,
          blob,
          objectUrl,
        })
      } catch (wasmErr: unknown) {
        if (wasmErr instanceof DOMException && wasmErr.name === 'AbortError') return
        if (!isCurrentOperation(generation, controller)) return

        toolError(utility, 'wasm_error', analyticsParams(currentLevel, {
          mode: 'wasm',
          failureCategory: 'processing_failed',
        }))

        setPhase({
          kind: 'error',
          file,
          level: currentLevel,
          errorCode: 'wasm_error',
          message: t.szhat.wasmError,
        })
      }
    } catch (classifyErr: unknown) {
      if (classifyErr instanceof DOMException && classifyErr.name === 'AbortError') return
      if (!isCurrentOperation(generation, controller)) return
      const encrypted = isEncryptedPdfError(classifyErr)

      toolError(utility, 'classify_error', analyticsParams(currentLevel, {
        mode: 'wasm',
        failureCategory: encrypted ? 'encrypted_input' : 'invalid_input',
      }))
      setPhase({
        kind: 'error',
        file,
        level: currentLevel,
        errorCode: 'classify_error',
        message: encrypted ? t.szhat.encryptedError : t.szhat.classifyError,
      })
    }
  }

  async function handleSubmit() {
    if (phase.kind !== 'ready') return
    await runLocalCompress(phase.file, level)
  }

  function handleTryStronger() {
    if (phase.kind !== 'success') return
    const file = phase.file
    replaceObjectUrl(null)
    setLevel('screen')
    void runLocalCompress(file, 'screen')
  }

  // ─── Render ────────────────────────────────────────────────────────────────

  if (phase.kind === 'noop') {
    return (
      <div className="space-y-4">
        <NoopPanel file={phase.file} targetBytes={targetBytes} onReset={handleClear} />
      </div>
    )
  }

  if (phase.kind === 'success') {
    return (
      <div className="space-y-4">
        <ResultPanel
          file={phase.file}
          blob={phase.blob}
          objectUrl={phase.objectUrl}
          level={phase.level}
          targetBytes={targetBytes}
          onReset={handleClear}
          onTryStronger={handleTryStronger}
        />
      </div>
    )
  }

  const loadingLabel =
    phase.kind === 'classifying' ? t.szhat.classifying : t.szhat.loadingLocal

  return (
    <div className="space-y-5">
      {/* Drop zone or file badge */}
      {currentFile ? (
        <FileBadge file={currentFile} onClear={handleClear} disabled={false} />
      ) : (
        <DropZone onFile={handleFile} disabled={isLoading} />
      )}

      <TargetSizeSelector
        value={targetPreset}
        onChange={(preset) => {
          setTargetPreset(preset)
          setLandingSource('direct')
          if (preset === '5mb') setLevel('screen')
          const url = new URL(window.location.href)
          if (preset === '5mb') url.searchParams.set('target', '5mb')
          else url.searchParams.delete('target')
          url.searchParams.delete('source')
          window.history.replaceState(window.history.state, '', url)
        }}
        disabled={phase.kind !== 'idle' && phase.kind !== 'ready'}
      />

      {/* Level selector — always visible so user can pre-select before picking file */}
      <LevelSelector value={level} onChange={setLevel} disabled={isLoading} />

      {/* Error panel */}
      {phase.kind === 'error' && (
        <ErrorPanel message={phase.message} onRetry={handleRetry} onCancel={handleClear} />
      )}

      {/* Submit */}
      <Button
        size="lg"
        className="w-full"
        disabled={phase.kind !== 'ready' || isLoading}
        onClick={handleSubmit}
      >
        {isLoading ? (
          <>
            <Spinner />
            {loadingLabel}
          </>
        ) : (
          t.szhat.submit
        )}
      </Button>
    </div>
  )
}
